import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  type PaginationState,
  useReactTable
} from '@tanstack/react-table'
import { FileText } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { useDebounceValue } from 'usehooks-ts'

import { adminEmployers } from 'rails_routes'
import { Button, TextInput } from 'slab'

import { useGetAdminIneligibleSpendReports, usePostAdminIneligibleSpendReportsEmployerIdGenerate, usePostAdminIneligibleSpendReportsEmployerIdPublish, usePostAdminIneligibleSpendReportsGenerateAll } from '../../utils/web_api'
import type { AdminIneligibleSpendEmployer, AdminIneligibleSpendEmployersList, AdminIneligibleSpendGenerationResponse, AdminIneligibleSpendPublishResponse } from '../../utils/web_api.schemas'
import TanstackTable, { type PaginationMetadata } from '../reusable/table/TanstackTable'

const columnHelper = createColumnHelper<AdminIneligibleSpendEmployer>()

// I'm putting this in here because (a) we've decided to disable publishing until
// we know what we want the comms to look like, but (b) we still want to be able to
// use this admin tool and know we will *eventually* publish the reports.
// When we're ready to enable publishing, we can remove this and unskip the relevant tests
const DISABLE_PUBLISH = true

const IneligibleSpendReports = () => {
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch] = useDebounceValue(searchInput, 300)
  const [publishingEmployerId, setPublishingEmployerId] = useState<string | null>(null)

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 1,
    pageSize: 25
  })

  const [paginationMetadata, setPaginationMetadata] = useState<PaginationMetadata | undefined>()

  // Reset pagination when search changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 1 }))
  }, [debouncedSearch])

  const {
    data,
    isLoading,
    error,
    refetch
  } = useGetAdminIneligibleSpendReports({
    'page[number]': pagination.pageIndex,
    'page[size]': pagination.pageSize,
    search: debouncedSearch || undefined
  })

  // Parse the response
  const response = data as unknown as AdminIneligibleSpendEmployersList | undefined
  const employers = response?.employers || []
  const responsePagination = response?.pagination

  // Mutation hooks for generating reports
  const generateAllMutation = usePostAdminIneligibleSpendReportsGenerateAll({
    mutation: {
      onSuccess: (data) => {
        const response = data as unknown as AdminIneligibleSpendGenerationResponse
        toast.success(response.message)
        refetch() // Refresh the list
      },
      onError: (error: any) => {
        console.error('Failed to generate all reports:', error)
        const errorMessage = error?.response?.data?.error || error?.message || 'Failed to start report generation. Please try again.'
        toast.error(errorMessage)
      }
    }
  })

  const generateSingleMutation = usePostAdminIneligibleSpendReportsEmployerIdGenerate({
    mutation: {
      onSuccess: (data) => {
        const response = data as unknown as AdminIneligibleSpendGenerationResponse
        toast.success(response.message)
        refetch() // Refresh the list
      },
      onError: (error: any) => {
        console.error('Failed to regenerate report:', error)
        const errorMessage = error?.response?.data?.error || error?.message || 'Failed to regenerate report. Please try again.'
        toast.error(errorMessage)
      }
    }
  })

  const publishMutation = usePostAdminIneligibleSpendReportsEmployerIdPublish({
    mutation: {
      onSuccess: (data) => {
        const response = data as unknown as AdminIneligibleSpendPublishResponse
        toast.success(response.message)
        setPublishingEmployerId(null) // Clear the publishing state
        refetch() // Refresh the list
      },
      onError: (error: any) => {
        console.error('Failed to publish report:', error)
        const errorMessage = error?.response?.data?.error || error?.message || 'Failed to publish report. Please try again.'
        toast.error(errorMessage)
        setPublishingEmployerId(null) // Clear the publishing state
      }
    }
  })

  // Column definitions
  const columns = useMemo(() => [
    columnHelper.accessor('name', {
      header: 'Employer Name',
      cell: (info) => (
        <div>
          <a
            href={adminEmployers.show({ id: info.row.original.id })}
            className='font-medium text-indigo-600 hover:text-indigo-900'
          >
            {info.getValue()}
          </a>
          <br />
          <div className='text-gray-500'>ID: {info.row.original.id}</div>
        </div>
      ),
      enableSorting: false
    }),
    columnHelper.display({
      id: 'draft_reports',
      header: 'Draft Ineligible Spend Report',
      cell: (info) => {
        const employer = info.row.original
        return (
          <div className='space-y-1'>
            {employer.draft_report_attachment_filename && (
              <div className='font-medium text-gray-900'>
                <a
                  href={adminEmployers.draftIneligibleSpendReport({ id: employer.id })}
                  className='text-indigo-600 hover:text-indigo-900'
                  download
                >
                  📊 {employer.draft_report_attachment_filename}
                </a>
              </div>
            )}

            {employer.draft_transaction_list_attachment_filename && (
              <div className='font-medium text-gray-900'>
                <a
                  href={adminEmployers.draftIneligibleSpendTransactionList({ id: employer.id })}
                  className='text-indigo-600 hover:text-indigo-900'
                  download
                >
                  📋 {employer.draft_transaction_list_attachment_filename}
                </a>
              </div>
            )}
          </div>
        )
      }
    }),
    columnHelper.display({
      id: 'actions',
      header: () => <div className='text-right'>Actions</div>,
      cell: (info) => {
        const employer = info.row.original
        return (
          <div className='flex gap-2 justify-end'>
            <Button
              onClick={() => handleRegenerateReport(employer.id)}
              disabled={generateSingleMutation.isPending}
              variant='tertiary'
              size='small'
            >
              {generateSingleMutation.isPending ? 'Generating...' : 'Regenerate'}
            </Button>
            {(employer.draft_report_attachment_filename || employer.draft_transaction_list_attachment_filename) && (
              <Button
                onClick={() => handlePublishReport(employer.id)}
                disabled={DISABLE_PUBLISH || publishingEmployerId === employer.id || generateSingleMutation.isPending}
                variant='primary'
                size='small'
              >
                {publishingEmployerId === employer.id ? 'Publishing...' : 'Publish'}
              </Button>
            )}
          </div>
        )
      }
    })
  ], [generateSingleMutation.isPending, publishingEmployerId])

  // Table setup
  const table = useReactTable({
    data: employers,
    columns,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => row.id,
    state: {
      pagination
    }
  })

  // Update pagination metadata when data changes
  useEffect(() => {
    if (responsePagination) {
      setPaginationMetadata(responsePagination)
    }
  }, [responsePagination])

  const handleGenerateAll = () => {
    const currentYear = new Date().getFullYear()
    generateAllMutation.mutate({
      data: { year: currentYear }
    })
  }

  const handleRegenerateReport = (employerId: string) => {
    const currentYear = new Date().getFullYear()
    generateSingleMutation.mutate({
      employerId,
      data: { year: currentYear }
    })
  }

  const handlePublishReport = (employerId: string) => {
    setPublishingEmployerId(employerId)
    publishMutation.mutate({
      employerId
    })
  }

  return (
    <div className='px-4 sm:px-6 lg:px-8'>
      <div className='sm:flex sm:items-center'>
        <div className='sm:flex-auto'>
          <h1 className='text-base font-semibold leading-6 text-gray-900'>
            Ineligible Spend Reports
          </h1>
          <p className='mt-2 text-sm text-gray-700'>
            Manage YTD ineligible spend reports for employers.
          </p>
        </div>
        <div className='mt-4 sm:ml-16 sm:mt-0 sm:flex-none'>
          <Button
            onClick={handleGenerateAll}
            disabled={generateAllMutation.isPending}
            variant='primary'
            size='large'
          >
            {generateAllMutation.isPending ? 'Starting Generation...' : 'Create Reports for All Employers'}
          </Button>
        </div>
      </div>

      {/* Search Input */}
      <div className='mt-6'>
        <div className='max-w-lg'>
          <TextInput
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder='Search employers...'
          />
        </div>
      </div>

      {/* Table */}
      <div className='mt-8'>
        <TanstackTable
          table={table}
          isLoading={isLoading}
          isError={!!error}
          errorMessage={error instanceof Error ? error.message : 'Unknown error occurred'}
          noDataMessage={
            <div className='py-8'>
              <FileText className='mx-auto h-12 w-12 text-gray-400' />
              <h3 className='mt-2 text-sm font-semibold text-gray-900'>
                No draft reports found
              </h3>
            </div>
          }
          paginationLabelSingular='employer'
          paginationLabelPlural='employers'
          paginationMetadata={paginationMetadata}
          onNextPageClick={() => setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.next_page || 1
          })}
          onPreviousPageClick={() => setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.prev_page || 1
          })}
        />
      </div>
    </div>
  )
}

export default IneligibleSpendReports
